import {
  Navigate,
} from 'react-router';

import {
  ProResourceType,
  ClientResourceType,
} from '@gymcloud/core';

import {
  UsersPage,
  VideosPage,
  PaymentsPage,
  ProductsPage,
  DashboardPage,
  ExercisesPage,
  MarketplacePage,
  EventResultsPage,
  PersonalWorkoutsPage,
  PersonalProgramsPage,

  // PRO
  TagPage,
  BillingPage,
  ClientsPage,
  MessagesPage,
  CalendarPage,
  DocumentPage,
  SearchResults,
  EntityFolderPage,
  ClientGroupsPage,
  DocumentsListPage,
  NewAnnouncementPage,
  WorkoutTemplatesPage,
  ProgramTemplatesPage,
  PersonalPropertiesPage,

  // CLIENT
  AccountPage,
  PersonalEntityPage,
} from 'web-containers';

import ProtectedRoute from './ProtectedRoute';

const proResourceTypes: { path: string, type: ProResourceType }[] = [
  {
    path: 'exercises_folder/:id',
    type: 'exercises',
  },
  {
    path: 'workout_templates_folder/:id',
    type: 'workoutTemplates',
  },
  {
    path: 'warmups_folder/:id',
    type: 'warmups',
  },
  {
    path: 'cooldowns_folder/:id',
    type: 'cooldowns',
  },
  {
    path: 'program_templates_folder/:id',
    type: 'programTemplates',
  },
];

const clientResourceTypes: { path: string, type: ClientResourceType }[] = [
  {
    type: 'personalExercises',
    path: 'personal_exercises',
  },
  {
    type: 'personalWarmups',
    path: 'personal_warmups',
  },
  {
    type: 'personalWorkouts',
    path: 'personal_workouts',
  },
  {
    type: 'personalCooldowns',
    path: 'personal_cooldowns',
  },
  {
    type: 'personalPrograms',
    path: 'personal_programs',
  },
];

const privateRoutes = [
  // COMMON ROUTES
  {
    path: 'welcome',
    meta: { track: true },
    element: <DashboardPage />,
  },
  {
    path: 'personal_workouts/:id/*',
    Component: PersonalWorkoutsPage,
  },
  {
    path: 'workout_exercises/:id/*',
    element: <ExercisesPage resourceType="workoutExercises" />,
  },
  {
    path: 'personal_programs/:id/*',
    Component: PersonalProgramsPage,
  },
  {
    path: 'events/:id/*',
    Component: EventResultsPage,
  },
  {
    path: 'workout_events/:id/*',
    Component: EventResultsPage,
  },
  {
    path: 'users/:id/*',
    Component: UsersPage,
  },
  {
    path: 'payments/*',
    meta: { track: true },
    Component: PaymentsPage,
  },
  {
    path: 'products',
    Component: ProductsPage,
  },
  {
    path: 'marketplace',
    meta: { track: true },
    Component: MarketplacePage,
  },
  {
    path: 'videos',
    element: <Navigate to="recent" replace />,
  },
  {
    path: 'videos/:order/*',
    meta: { track: true },
    element: (
      <ProtectedRoute allowedFeatures="video_library">
        <VideosPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'videos/:order/:page/*',
    meta: { track: true },
    element: (
      <ProtectedRoute allowedFeatures="video_library">
        <VideosPage />
      </ProtectedRoute>
    ),
  },

  // PRO ROUTES
  {
    path: 'billing',
    element: <Navigate to="invoices" replace />,
  },
  {
    path: 'billing/*',
    meta: { track: true },
    element: (
      <ProtectedRoute allowedFeatures="billing_payment">
        <BillingPage />
      </ProtectedRoute>
    ),
  },
  ...proResourceTypes.map(
    resource => ({
      path: resource.path,
      element: <EntityFolderPage resourceType={resource.type} />,
    }),
  ),
  {
    path: 'clients',
    element: <Navigate to="individuals" replace />,
  },
  {
    path: 'clients/*',
    meta: { track: true },
    Component: ClientsPage,
  },
  {
    path: 'client_groups/:id/*',
    meta: { track: true },
    Component: ClientGroupsPage,
  },
  {
    path: 'messages',
    meta: { track: true },
    element: (
      <ProtectedRoute allowedFeatures="messages">
        <MessagesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'messages/:id/:category',
    meta: { track: true },
    element: (
      <ProtectedRoute allowedFeatures="messages">
        <MessagesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'calendar',
    meta: { track: true },
    element: (
      <ProtectedRoute allowedFeatures="calendar_and_scheduling">
        <CalendarPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'calendar/:date/:id',
    meta: { track: true },
    Component: CalendarPage,
  },
  {
    path: 'exercises/:id/*',
    element: <ExercisesPage resourceType="exercises" />,
  },
  {
    path: 'workout_templates/:id/*',
    element: <WorkoutTemplatesPage resourceType="workoutTemplates" />,
  },
  {
    path: 'warmups/:id/*',
    element: <WorkoutTemplatesPage resourceType="warmups" />,
  },
  {
    path: 'cooldowns/:id/*',
    element: <WorkoutTemplatesPage resourceType="cooldowns" />,
  },
  {
    path: 'program_templates/:id/*',
    Component: ProgramTemplatesPage,
  },
  {
    path: 'personal_properties',
    meta: { track: true },
    Component: PersonalPropertiesPage,
  },
  {
    path: 'search/:searchQuery/*',
    Component: SearchResults,
  },
  {
    path: 'tags/:tag/*',
    meta: { track: true },
    Component: TagPage,
  },
  {
    path: 'documents/:id/*',
    Component: DocumentPage,
  },
  {
    path: 'new_announcement',
    meta: { track: true },
    Component: NewAnnouncementPage,
  },

  // CLIENT ROUTES
  ...clientResourceTypes.map(
    resource => ({
      path: resource.path,
      meta: { track: true },
      element: <PersonalEntityPage resourceType={resource.type} />,
    }),
  ),
  {
    path: 'personal_exercises/:id/*',
    element: <ExercisesPage requestType="exercises" resourceType="personalExercises" />,
  },
  {
    path: 'personal_warmups/:id/*',
    options: {
      type: 'personalWorkouts',
      addtionalType: 'personalWarmups',
    },
    element: <PersonalWorkoutsPage addtionalType="personalWarmups" />,
  },
  {
    path: 'personal_cooldowns/:id/*',
    element: <PersonalWorkoutsPage addtionalType="personalCooldowns" />,
  },
  {
    path: 'account/*',
    meta: { track: true },
    Component: AccountPage,
  },
  {
    path: 'documents',
    meta: { track: true },
    element: (
      <ProtectedRoute allowedFeatures="documents">
        <DocumentsListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'documents_folder/:id',
    element: (
      <ProtectedRoute allowedFeatures="documents">
        <DocumentsListPage />
      </ProtectedRoute>
    ),
  },
];

export default privateRoutes;
